import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { CircleNotch } from "@phosphor-icons/react";
import SkipVisitBlock from "./SkipVisitBlock";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SlotPicker from "./SlotPicker";
import { fmtPrice, isDemoMode } from "@/components/DemoHelpers";


const fmt = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

/**
 * Stage "purchase": form finale di acquisto (plafond o condivisione cliente),
 * con optional slot picker per sopralluogo e markup cliente.
 */
export default function PurchaseStage({
  selected,
  properties,
  user,
  plafond = null,
  isSubUser,
  form,
  subMarginPct,
  visitOnFirstStep,
  slotMonth,
  monthGrid,
  selectedDate,
  slotsForSelectedDate,
  chosenSlot,
  totalSlotsCount,
  onSubmit,
  onBackToPayment,
  onBackToProperty,
  onPatchForm,
  onSetSubMargin,
  onSetSlotMonth,
  onSetSelectedDate,
  onSetChosenSlot,
  // Iter65 — salto sopralluogo
  canSkipVisit = false,
  housingFeeRate = 0.3,
  isPurchasing = false,
  skipVisitNote = "",
  visitSkipped = false,
  // Iter126: totale calcolato dal PurchaseModal includendo il delta del
  // Prezzo Flessibile. Se non fornito, cadiamo sul `professional_price` base.
  totalPrice = null,
  canOverridePrice = false,
}) {
  const [feeHintOpen, setFeeHintOpen] = useState(false);
  const pp = properties.find((p) => p._id === form.property_id);
  const calculatedPrice = Number(
    totalPrice ?? selected.professional_price ?? selected.base_price ?? 0,
  );
  const overrideRaw = form.professional_price_override;
  const hasOverride = canOverridePrice && overrideRaw !== null && overrideRaw !== "" && Number(overrideRaw) > 0;
  const isPriceModified = hasOverride && Number(overrideRaw) !== calculatedPrice;
  // `unitPrice` è il costo unitario effettivo del servizio per il PRO,
  // includendo eventuali modifier del Prezzo Flessibile o l'override admin.
  const unitPrice = hasOverride ? Number(overrideRaw) : calculatedPrice;

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      {/* Iter209e4 — Banner "Modalità: …" nascosto: il ritorno a payment_choice
          resta garantito da "← Indietro". */}

      {/* Step summary per la modalità share: chiarisce che il cliente
        * prenoterà lo slot autonomamente dopo il pagamento. Evita l'ambiguità
        * "dove fisso io lo slot?" e la trigger del bug slot-fantasma. */}
      {form.is_shared_with_client && (
        <div
          className="p-3 rounded-md border border-blue-200 bg-blue-50 text-[12px] text-blue-900 leading-relaxed"
          data-testid="share-mode-hint"
        >
          <div className="font-semibold mb-1">Come funziona la modalità Condividi</div>
          <ol className="list-decimal ml-5 space-y-0.5">
            <li>Imposti il prezzo finale per il cliente.</li>
            <li>Ricevi un <strong>link di pagamento</strong> da inviare al cliente.</li>
            <li>Dopo il pagamento, <strong>il cliente sceglie data e orario</strong> del sopralluogo dal calendario pubblico.</li>
          </ol>
        </div>
      )}

      <div className="p-3 bg-purple-50 border border-purple-200 rounded-md text-xs">
        <div>
          <strong>Immobile:</strong>{" "}
          {pp ? `${pp.address}, ${pp.city} — ${pp.owner_first_name} ${pp.owner_last_name}` : "—"}
        </div>
        <button
          type="button"
          onClick={onBackToProperty}
          className="text-purple-700 hover:underline mt-1"
        >
          Cambia immobile
        </button>
      </div>

      {isSubUser && (user?.subuser_can_add_margin || user?.subuser_can_sell_below_cost) && (() => {
        const canAdd = !!user?.subuser_can_add_margin;
        const canBelow = !!user?.subuser_can_sell_below_cost;
        const maxPct = Number(user?.subuser_max_margin_pct || 25);
        // Range consentito:
        //  - canBelow && canAdd  → [-100, maxPct]
        //  - canBelow && !canAdd → [-100, 0]
        //  - !canBelow && canAdd → [0, maxPct]
        //  - !canBelow && !canAdd → [0, 0]  (bloccato al prezzo di listino)
        const minPct = canBelow ? -100 : 0;
        const maxAllowed = canAdd ? maxPct : 0;
        const netPrice = Number(selected.professional_price || 0);
        const subAmt = (netPrice * (Number(subMarginPct) || 0)) / 100;
        const finalPrice = netPrice + subAmt;
        return (
          <div className="p-3 rounded-lg border-2 border-purple-200 bg-purple-50/40" data-testid="sub-margin-panel">
            <div className="font-semibold text-sm text-purple-900 mb-1">Il tuo margine sul cliente</div>
            <p className="text-xs text-muted-foreground mb-2">
              {canAdd && canBelow && `Puoi impostare un margine fino a +${maxPct}% o scendere sottocosto (il tuo professionista coprirà la differenza).`}
              {canAdd && !canBelow && `Puoi aggiungere una percentuale sopra al prezzo (max ${maxPct}%). Il credito accumulato verrà tracciato sul conto del tuo professionista.`}
              {!canAdd && canBelow && "Il tuo professionista consente vendite sottocosto: puoi solo scontare il prezzo (0% o negativo)."}
            </p>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={minPct}
                max={maxAllowed}
                step="1"
                value={subMarginPct}
                onChange={(e) => onSetSubMargin(e.target.value)}
                className="w-24"
                data-testid="sub-margin-input"
              />
              <span className="text-sm">%</span>
              <span className="text-xs text-muted-foreground ml-3">
                {subAmt >= 0 ? "+" : ""}€ {subAmt.toFixed(2)} → Cliente paga{" "}
                <strong className="text-purple-800">€ {finalPrice.toFixed(2)}</strong>
              </span>
            </div>
            {finalPrice < netPrice && (
              <div
                className="mt-2 rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-xs px-3 py-2 leading-relaxed"
                data-testid="sub-below-cost-warning"
              >
                ⚠️ Attenzione: la differenza tra il tuo costo e il prezzo per il cliente verrà addebitata sul plafond del tuo professionista.
              </div>
            )}
          </div>
        );
      })()}

      {/* Iter209e4 — Il blocco "salta sopralluogo" è in payment_choice
          (PurchaseModal). I sub-user non passano da lì: lo mostriamo qui. */}
      {isSubUser && (
        <SkipVisitBlock
          canSkipVisit={canSkipVisit}
          visitSkipped={visitSkipped}
          skipVisitNote={skipVisitNote}
        />
      )}

      {visitOnFirstStep && !form.is_shared_with_client && !visitSkipped && (
        <SlotPicker
          slotMonth={slotMonth}
          monthGrid={monthGrid}
          selectedDate={selectedDate}
          slotsForSelectedDate={slotsForSelectedDate}
          chosenSlot={chosenSlot}
          totalSlotsCount={totalSlotsCount}
          onSetSlotMonth={onSetSlotMonth}
          onSetSelectedDate={onSetSelectedDate}
          onSetChosenSlot={onSetChosenSlot}
        />
      )}

      <div className="p-3 bg-[color:hsl(var(--surface-alt))] rounded-md text-sm">
        <div className="flex justify-between items-center gap-2">
          <span>Costo servizio</span>
          {canOverridePrice ? (
            <span className="flex items-center gap-1 font-mono whitespace-nowrap">
              €
              <input
                type="number" min="0.01" step="0.01"
                value={overrideRaw ?? calculatedPrice}
                onChange={(e) => onPatchForm({ professional_price_override: e.target.value })}
                className="w-28 h-8 border rounded-md px-2 text-right bg-white"
                data-testid="purchase-stage-service-cost-input"
              />
              <span className="ml-1 text-[0.7em] font-sans font-normal text-slate-500" data-testid="purchase-stage-service-cost-iva">+ IVA</span>
            </span>
          ) : (
            <span className="font-mono whitespace-nowrap"><span data-testid="purchase-stage-service-cost">{fmtPrice(unitPrice)}</span><span className="ml-1 text-[0.7em] font-sans font-normal text-slate-500" data-testid="purchase-stage-service-cost-iva">+ IVA</span></span>
          )}
        </div>
        {isPriceModified && (
          <div className="text-[11px] text-amber-700 text-right mt-1" data-testid="purchase-stage-price-modified-note">
            Prezzo modificato manualmente (calcolato: {fmtPrice(calculatedPrice)})
          </div>
        )}
        {canOverridePrice && overrideRaw !== null && overrideRaw !== "" && !(Number(overrideRaw) > 0) && (
          <div className="text-[11px] text-red-600 text-right mt-1" data-testid="purchase-stage-price-invalid-note">
            Inserisci un prezzo maggiore di 0: verrà usato il prezzo calcolato
          </div>
        )}
        {form.is_shared_with_client && (() => {
          const proPrice = unitPrice;
          // Iter209dzt — Contributo Housetag (30% del prezzo pro): componente
          // separata, NON fa parte del client_markup né del credito del pro.
          const housingFee = Math.round(proPrice * housingFeeRate * 100) / 100;
          const basePrice = +(proPrice + housingFee).toFixed(2);
          const margin = Number(form.client_markup) || 0;
          const clientPrice = +(basePrice + margin).toFixed(2);
          const belowCost = margin < 0;
          return (
            <div className="mt-3 relative z-10" data-testid="markup-inline">
              <div
                className="flex justify-between items-center mb-3 px-3 py-2 rounded-md border border-slate-200 bg-white/70 text-xs"
                data-testid="housing-fee-row"
              >
                <span className="text-slate-700">
                  Contributo di fatturazione Housetag <span className="text-slate-400" data-testid="housing-fee-pct">(+{Math.round(housingFeeRate * 1000) / 10}%)</span>
                </span>
                <span className="font-mono font-semibold" data-testid="housing-fee-value">{fmtPrice(housingFee)}</span>
              </div>
              <Label className="text-xs">Prezzo per il cliente (imponibile, IVA esclusa) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={clientPrice}
                onChange={(e) => {
                  // Iter59: consentiamo prezzi < costo (vendita sottocosto).
                  // La maggiorazione pro è calcolata al netto del contributo Housetag.
                  const raw = Number(e.target.value);
                  const v = Number.isFinite(raw) ? raw : 0;
                  onPatchForm({ client_markup: +(v - basePrice).toFixed(2) });
                }}
                onBlur={(e) => {
                  const raw = Number(e.target.value);
                  const v = Math.max(0, Number.isFinite(raw) ? raw : 0);
                  const nextMarkup = +(v - basePrice).toFixed(2);
                  if (nextMarkup !== margin) {
                    onPatchForm({ client_markup: nextMarkup });
                  }
                  setFeeHintOpen(false);
                }}
                onFocus={() => setFeeHintOpen(true)}
                onClick={() => setFeeHintOpen(true)}
                data-testid="purchase-client-price-input"
                placeholder={`suggerito ≥ ${fmt(basePrice)}`}
                className="mt-1 relative z-10 pointer-events-auto"
              />
              {feeHintOpen && (
                <div
                  className="mt-2 rounded-md border border-sky-300 bg-sky-50 text-sky-900 text-xs px-3 py-2 leading-relaxed fade-up"
                  role="status"
                  data-testid="client-price-fee-hint"
                >
                  ⚠️ Attenzione: se prevedi di condividere il link di pagamento con il tuo cliente, ricordati di calcolare la maggiorazione del {Math.round(housingFeeRate * 1000) / 10}% sul prezzo base di {fmtPrice(proPrice)}.
                </div>
              )}
              {belowCost && (
                <div
                  className="mt-2 rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-xs px-3 py-2 leading-relaxed"
                  data-testid="below-cost-warning"
                >
                  ⚠️ Attenzione: la differenza tra il tuo costo (incluso il contributo Housetag) e il prezzo per il cliente verrà addebitata sul tuo plafond.
                </div>
              )}
              <div className="mt-2 flex items-center gap-2 text-xs flex-wrap" data-testid="markup-summary">
                <span className="text-muted-foreground">Prezzo per te:</span>
                <span className="font-mono font-semibold">{fmtPrice(proPrice)}</span>
                <span className="text-purple-500">+</span>
                <span className="text-muted-foreground">Housetag:</span>
                <span className="font-mono font-semibold">{fmtPrice(housingFee)}</span>
                <span className="text-purple-500">→</span>
                <span className="text-muted-foreground">Prezzo finale:</span>
                <span className="font-mono font-semibold text-purple-700" data-testid="client-final-price">{fmt(clientPrice)}</span>
                <span
                  className={`px-1.5 py-0.5 rounded font-mono ${margin >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}
                  data-testid="pro-markup-badge"
                >
                  Tua maggiorazione: {margin >= 0 ? "+" : ""}
                  {isDemoMode() ? "€ ***" : fmt(margin)}
                </span>
              </div>
            </div>
          );
        })()}
        {!form.is_shared_with_client && (
          <div className="flex justify-between mt-2 pt-2 border-t font-semibold">
            <span>Acquisto tramite plafond</span>
            <span className="font-mono" data-testid="purchase-stage-plafond-total">{fmtPrice(unitPrice)}</span>
          </div>
        )}
      </div>

      {/* Breakdown plafond mensile: mostrato SOLO ai pro parent (non ai sub-user),
          in modalità plafond (non condivisione cliente), e solo se abbiamo i dati plafond. */}
      {!form.is_shared_with_client && !isSubUser && plafond && !isDemoMode() && (() => {
        const monthly = Number(plafond.monthly_limit || 0);
        const usedBefore = Number(user?.plafond_used_current_month || 0);
        const cost = unitPrice;
        const remaining = Math.max(0, monthly - usedBefore - cost);
        const overspend = monthly > 0 && (usedBefore + cost) > monthly;
        return (
          <div
            className={`rounded-lg border p-3 text-xs ${overspend ? "border-red-300 bg-red-50" : "border-slate-200 bg-white"}`}
            data-testid="plafond-breakdown"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold text-[11px] uppercase tracking-widest text-slate-500">
                Riepilogo plafond
              </div>
              <div className="text-[10px] text-slate-400">{plafond.name}</div>
            </div>
            <BreakRow label="Plafond mensile" value={fmt(monthly)} />
            <BreakRow label="Già utilizzato" value={fmt(usedBefore)} />
            <BreakRow label="Costo di questo servizio" value={`− ${fmt(cost)}`} accent="purple" />
            <div className="h-px bg-slate-200 my-1.5" />
            <BreakRow
              label={overspend ? "Sfori il plafond di" : "Plafond rimanente dopo l'acquisto"}
              value={overspend ? `− ${fmt((usedBefore + cost) - monthly)}` : fmt(remaining)}
              accent={overspend ? "red" : "emerald"}
              bold
            />
            {overspend && (
              <div className="mt-2 text-[11px] text-red-700 font-semibold" data-testid="plafond-overspend-warning">
                ⚠️ Attenzione: stai sconfinando con il plafond mensile.
              </div>
            )}
          </div>
        );
      })()}

      {!form.is_shared_with_client ? (
        <div className="pt-1 space-y-2">
          <Button
            type="submit"
            className="w-full ht-gradient border-0 py-6"
            disabled={isPurchasing}
            aria-busy={isPurchasing}
            data-testid="purchase-confirm-plafond"
          >
            <div className="text-left w-full">
              <div className="font-semibold flex items-center gap-2">
                {isPurchasing && <CircleNotch size={16} className="animate-spin" data-testid="purchase-plafond-spinner" />}
                {isPurchasing ? "Acquisto in corso…" : "Acquista con il tuo plafond"}
              </div>
              {!isDemoMode() && (
                <div className="text-[11px] opacity-90 font-normal">
                  Addebito {fmt(unitPrice)} <span data-testid="purchase-plafond-iva">+ IVA</span> sul plafond mensile
                </div>
              )}
            </div>
          </Button>
        </div>
      ) : (
        <div className="space-y-3 pt-2">
          <Button
            type="submit"
            className="w-full ht-gradient border-0"
            disabled={isPurchasing}
            aria-busy={isPurchasing}
            data-testid="purchase-confirm-share"
          >
            {isPurchasing && <CircleNotch size={16} className="animate-spin mr-2" data-testid="purchase-share-spinner" />}
            {isPurchasing ? "Generazione link in corso…" : "Genera link di pagamento"}
          </Button>
        </div>
      )}
      <div className="flex justify-start pt-1">
        <button
          type="button"
          onClick={onBackToPayment}
          className="text-xs text-muted-foreground hover:text-foreground"
          data-testid="purchase-back-btn"
        >
          ← Indietro
        </button>
      </div>
    </form>
  );
}

const ACCENT_CLS = {
  purple: "text-purple-700",
  emerald: "text-emerald-700",
  red: "text-red-700",
};

function BreakRow({ label, value, accent, bold = false }) {
  const cls = ACCENT_CLS[accent] || "text-slate-700";
  return (
    <div className="flex justify-between items-center py-0.5">
      <span className={`${bold ? "font-semibold" : ""} text-slate-600`}>{label}</span>
      <span className={`font-mono ${cls} ${bold ? "font-bold" : ""}`}>{value}</span>
    </div>
  );
}

